import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, message, Spin, Statistic, Typography } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getConfig } from '../../config'

const { Title, Text } = Typography

const BREEZY_LOGO_URL = 'https://s3.us-west-1.amazonaws.com/www.getbreezyapp.com/images/Breezy-01.png'

type QbdUptimeCheck = {
  uptimeCheckGuid: string
  companyGuid: string
  conductorEndUserId: string
  isConnected: boolean
  checkedAt: string
  responseTimeMs?: number
  errorMessage?: string
}

type QbdStatusResponse = {
  companyName: string
  currentStatus: 'connected' | 'disconnected'
  lastCheckedAt: string
  uptimePercentage: number
  last7Days: QbdUptimeCheck[]
}

const UptimeChart = React.memo<{
  status: QbdStatusResponse
  checks: QbdUptimeCheck[]
}>(({ status, checks }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [hoveredBlock, setHoveredBlock] = useState<{
    x: number
    y: number
    status: string
    time: string
    responseTime?: number
    uptimePercentage?: number
    checksCount?: number
    isLatest?: boolean
  } | null>(null)

  const chartData = useMemo(() => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    // Access status data directly to avoid type conflicts

    // Create timeline with 4-hour intervals (6 blocks per day = 42 total blocks)
    const timelineData: Array<{
      time: Date
      status: 'up' | 'down' | 'unknown'
      uptimePercentage: number
      checksInPeriod: QbdUptimeCheck[]
      isLatest: boolean
    }> = []

    // Generate 4-hour slots for the past 7 days
    for (let time = new Date(sevenDaysAgo); time <= now; time.setHours(time.getHours() + 4)) {
      timelineData.push({
        time: new Date(time),
        status: 'unknown',
        uptimePercentage: 0,
        checksInPeriod: [],
        isLatest: false,
      })
    }

    // Mark the latest slot
    if (timelineData.length > 0) {
      timelineData[timelineData.length - 1].isLatest = true
    }

    // Group checks by timeline slots
    checks.forEach(check => {
      const checkTime = new Date(check.checkedAt).getTime()

      // Find all slots this check could belong to (within the 4-hour window)
      timelineData.forEach(slot => {
        const slotStart = slot.time.getTime()
        const slotEnd = slotStart + 4 * 60 * 60 * 1000 // 4 hours

        if (checkTime >= slotStart && checkTime < slotEnd) {
          slot.checksInPeriod.push(check)
        }
      })
    })

    // Calculate uptime percentage for each slot
    timelineData.forEach(slot => {
      if (slot.isLatest) {
        // For latest slot, always use current status (perfect green/red)
        // Access status directly from the state variable
        if (status && 'currentStatus' in status && status.currentStatus === 'connected') {
          slot.status = 'up'
          slot.uptimePercentage = 1 // Perfect green
        } else if (status && 'currentStatus' in status && status.currentStatus === 'disconnected') {
          slot.status = 'down'
          slot.uptimePercentage = 0 // Perfect red
        } else {
          slot.status = 'unknown'
          slot.uptimePercentage = 0.5 // Grey
        }
      } else if (slot.checksInPeriod.length === 0) {
        slot.status = 'unknown'
        slot.uptimePercentage = 0
      } else {
        const successfulChecks = slot.checksInPeriod.filter(check => check.isConnected).length
        slot.uptimePercentage = successfulChecks / slot.checksInPeriod.length
        // For past slots, use majority rule
        slot.status = slot.uptimePercentage >= 0.5 ? 'up' : 'down'
      }
    })

    return timelineData
  }, [checks, status])

  const dayLabels = useMemo(() => {
    const daysShown = 7
    const blocksPerDay = chartData.length / daysShown
    const labels = []

    for (let day = 0; day < daysShown; day++) {
      const dayIndex = Math.floor(day * blocksPerDay)
      if (dayIndex < chartData.length) {
        const dayData = chartData[dayIndex]
        const percentage = ((dayIndex + blocksPerDay / 2) / chartData.length) * 100

        labels.push({
          label: dayData.time.toLocaleDateString([], {
            month: 'short',
            day: 'numeric',
          }),
          position: percentage,
        })
      }
    }

    return labels
  }, [chartData])

  // Helper function to interpolate between red and green based on uptime percentage
  const getUptimeColor = (uptimePercentage: number, isLatest: boolean, blockStatus: 'up' | 'down' | 'unknown') => {
    // For latest block, use current status colors
    if (isLatest) {
      switch (blockStatus) {
        case 'up':
          return '#52c41a' // Green
        case 'down':
          return '#ff4d4f' // Red
        default:
          return '#d9d9d9' // Grey
      }
    }

    // For past blocks, use gradient if there were checks
    if (blockStatus === 'unknown') {
      return '#d9d9d9' // Grey for no data
    }

    // Interpolate between red (0%) and green (100%)
    const red = Math.round(255 * (1 - uptimePercentage))
    const green = Math.round(200 * uptimePercentage)
    const blue = 50

    return `rgb(${red}, ${green}, ${blue})`
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const canvasWidth = canvas.width
    const canvasHeight = canvas.height
    const blockWidth = canvasWidth / chartData.length
    const blockHeight = 30

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)

    // Draw status blocks with gradient colors
    chartData.forEach((data, index) => {
      const x = index * blockWidth
      const y = 5

      const color = getUptimeColor(data.uptimePercentage, data.isLatest, data.status)

      ctx.fillStyle = color
      ctx.fillRect(x, y, blockWidth - 1, blockHeight)
    })
    // biome-ignore lint/correctness/useExhaustiveDependencies: getUptimeColor changes on every re-render but is intentionally included
  }, [chartData, getUptimeColor])

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const blockWidth = canvas.width / chartData.length
    const blockIndex = Math.floor(x / blockWidth)

    if (blockIndex >= 0 && blockIndex < chartData.length && y >= 5 && y <= 35) {
      const data = chartData[blockIndex]
      const statusText = data.status === 'up' ? 'Connected' : data.status === 'down' ? 'Disconnected' : 'Unknown'

      setHoveredBlock({
        x: event.clientX,
        y: event.clientY,
        status: statusText,
        time: data.time.toLocaleString(),
        uptimePercentage: data.uptimePercentage,
        checksCount: data.checksInPeriod.length,
        isLatest: data.isLatest,
      })
    } else {
      setHoveredBlock(null)
    }
  }

  const handleMouseLeave = () => {
    setHoveredBlock(null)
  }

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={600}
        height={40}
        className="h-10 w-full cursor-pointer"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ maxWidth: '100%' }}
      />

      {/* Day labels underneath */}
      <div className="relative mt-1 h-5">
        {dayLabels.map((day, index) => (
          <div
            key={index}
            className="absolute text-xs text-gray-600"
            style={{
              left: `${day.position}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {day.label}
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {hoveredBlock && (
        <div
          className="pointer-events-none absolute z-10 rounded border border-gray-300 bg-white p-2 text-xs shadow-lg"
          style={{
            left: hoveredBlock.x - 150,
            top: hoveredBlock.y - 80,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="font-medium">{hoveredBlock.status}</div>
          <div className="text-gray-600">{hoveredBlock.time}</div>
          {hoveredBlock.isLatest ? (
            <div className="text-gray-600">Current status</div>
          ) : !!hoveredBlock.checksCount && hoveredBlock.checksCount > 0 ? (
            <>
              <div className="text-gray-600">Uptime: {Math.round((hoveredBlock.uptimePercentage || 0) * 100)}%</div>
              <div className="text-gray-600">
                {hoveredBlock.checksCount} check
                {hoveredBlock.checksCount !== 1 ? 's' : ''}
              </div>
            </>
          ) : (
            <div className="text-gray-600">No data</div>
          )}
        </div>
      )}
    </div>
  )
})

export const QbdStatusPage = React.memo(() => {
  const { companyGuid } = useParams<{ companyGuid: string }>()
  const [status, setStatus] = useState<QbdStatusResponse | undefined>()
  const [loading, setLoading] = useState(true)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [testInitiatedAt, setTestInitiatedAt] = useState<Date | null>(null)

  const fetchStatus = useCallback(async () => {
    if (!companyGuid) return

    try {
      setLoading(true)
      setError(undefined)

      const apiUrl = getConfig().apiUrl
      const response = await fetch(`${apiUrl}/qbd-status/${companyGuid}`)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch status')
      }

      const data: QbdStatusResponse = await response.json()
      setStatus(data)

      // Check if we have a new result after test was initiated
      if (testInitiatedAt && testing) {
        const lastCheckedAt = new Date(data.lastCheckedAt)
        if (lastCheckedAt > testInitiatedAt) {
          // We have a new result, stop testing state
          setTesting(false)
          setTestInitiatedAt(null)

          // Show success/failure message
          if (data.currentStatus === 'connected') {
            message.success('Connection test completed successfully!')
          } else {
            message.error('Connection test failed')
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      message.error(`Failed to load status: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }, [companyGuid, testInitiatedAt, testing])

  const testConnection = useCallback(async () => {
    if (!companyGuid || testing) return

    try {
      setTesting(true)
      setTestInitiatedAt(new Date())

      const apiUrl = getConfig().apiUrl

      // Fire and forget - don't wait for response
      fetch(`${apiUrl}/qbd-status/${companyGuid}/test`, {
        method: 'POST',
      }).catch(err => {
        console.error('Test connection request failed:', err)
        setTesting(false)
        setTestInitiatedAt(null)
        message.error('Failed to initiate connection test')
      })

      message.info('Connection test initiated...')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      message.error(`Failed to start test: ${errorMessage}`)
      setTesting(false)
      setTestInitiatedAt(null)
    }
  }, [companyGuid, testing])

  // Initial load and polling
  useEffect(() => {
    fetchStatus()

    // Poll more frequently when testing (5 seconds), otherwise every 30 seconds
    const pollInterval = testing ? 5000 : 30000
    const interval = setInterval(fetchStatus, pollInterval)
    return () => clearInterval(interval)
  }, [fetchStatus, testing])

  const formatLastChecked = (timestamp: string) => {
    return new Date(timestamp).toLocaleString()
  }

  const getStatusColor = (isConnected: boolean) => {
    return isConnected ? '#52c41a' : '#ff4d4f'
  }

  const getStatusIcon = (isConnected: boolean) => {
    return isConnected ? <CheckCircleOutlined /> : <CloseCircleOutlined />
  }

  // Check if the last check was a failure
  const lastCheckError = useMemo(() => {
    if (!status?.last7Days?.length) return null

    const sortedChecks = [...status.last7Days].sort(
      (a, b) => new Date(b.checkedAt).getTime() - new Date(a.checkedAt).getTime(),
    )

    const lastCheck = sortedChecks[0]
    return !lastCheck.isConnected ? lastCheck.errorMessage : null
  }, [status?.last7Days])

  if (!companyGuid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Alert message="Invalid company GUID" type="error" />
      </div>
    )
  }

  if (error && !status) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Card>
          <Alert
            message="Error Loading Status"
            description={error}
            type="error"
            action={
              <Button onClick={fetchStatus} type="primary">
                Retry
              </Button>
            }
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 w-full">
          <div className="mb-4 flex w-full items-center justify-between">
            <img src={BREEZY_LOGO_URL} alt="Breezy" className="mr-6 h-8" />
            <div className="text-right">
              <Title level={2} className="mb-2">
                QuickBooks Desktop Connection Status
              </Title>
              {status && (
                <Text type="secondary" className="text-lg">
                  {status.companyName}
                </Text>
              )}
            </div>
          </div>
        </div>

        {loading && !status ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : (
          status && (
            <div className="space-y-6">
              {/* Current Status Card */}
              <Card>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className="text-4xl"
                      style={{
                        color: getStatusColor(status.currentStatus === 'connected'),
                      }}
                    >
                      {getStatusIcon(status.currentStatus === 'connected')}
                    </div>
                    <div className="flex-1">
                      <Title level={3} className="mb-1">
                        {status.currentStatus === 'connected' ? 'Connected' : 'Disconnected'}
                      </Title>
                      <Text type="secondary">Last checked: {formatLastChecked(status.lastCheckedAt)}</Text>
                    </div>
                  </div>
                  <div className="text-center">
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      loading={testing}
                      onClick={testConnection}
                      size="large"
                    >
                      Test Connection
                    </Button>
                    <div className="mt-1 text-xs text-gray-400">Takes up to 60 seconds</div>
                  </div>
                </div>
                {/* Error message integrated into status card */}
                {lastCheckError && (
                  <div className="mt-6 rounded border border-red-200 bg-red-50 p-3">
                    <div className="flex items-start space-x-2">
                      <CloseCircleOutlined className="mt-0.5 text-sm text-red-500" />
                      <div>
                        <div className="text-sm font-medium text-red-800">Connection Issue Detected</div>
                        <div className="mt-1 text-sm text-red-700">{lastCheckError}</div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              {/* Uptime Statistics */}
              <Card title="7-Day Uptime Statistics">
                <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                  <Statistic
                    title="Uptime Percentage"
                    value={status.uptimePercentage}
                    suffix="%"
                    valueStyle={{
                      color: status.uptimePercentage >= 95 ? '#3f8600' : '#cf1322',
                    }}
                  />
                  <Statistic title="Total Checks" value={status.last7Days.length} />
                  <Statistic
                    title="Successful Connections"
                    value={status.last7Days.filter(check => check.isConnected).length}
                  />
                </div>
                <div>
                  <Text strong className="mb-2 block text-sm">
                    Connection Timeline
                  </Text>
                  <UptimeChart status={status} checks={status.last7Days} />
                </div>
              </Card>

              {/* Recent History */}
              <Card title="Recent Connection History">
                <div className="space-y-2">
                  {status.last7Days.slice(0, 10).map(check => (
                    <div
                      key={check.uptimeCheckGuid}
                      className="flex items-center justify-between rounded bg-gray-50 p-3"
                    >
                      <div className="flex items-center space-x-3">
                        <span style={{ color: getStatusColor(check.isConnected) }}>
                          {getStatusIcon(check.isConnected)}
                        </span>
                        <div>
                          <div className="font-medium">{check.isConnected ? 'Connected' : 'Failed'}</div>
                          <div className="text-sm text-gray-500">{formatLastChecked(check.checkedAt)}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        {check.responseTimeMs && <div className="text-sm text-gray-600">{check.responseTimeMs}ms</div>}
                        {check.errorMessage && (
                          <div className="max-w-xs truncate text-sm text-red-500">{check.errorMessage}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {status.last7Days.length === 0 && (
                    <div className="py-8 text-center text-gray-500">No connection history available yet</div>
                  )}
                </div>
              </Card>

              {/* Auto-refresh notice */}
              <div className="text-center">
                <Text type="secondary" className="text-sm">
                  Status automatically updates every {testing ? '5 seconds' : '30 seconds'}
                </Text>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
})

QbdStatusPage.displayName = 'QbdStatusPage'
